import {
    styled, Button, Stack, Box,
    IconButton,
    InputAdornment,
    TextField,
    Alert,
    Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
    useCallback, useEffect, useState,
    useTransition,
} from 'react';
import debounce from 'lodash/debounce';

import { RelevanceTestDefinitionSearchResults } from 'src/components/RelevanceTestDefinitionSearchPage/RelevanceTestDefinitionSearchResults';
import { RelevanceTestDefinitionSearchResultsSkeleton } from 'src/components/RelevanceTestDefinitionSearchPage/RelevanceTestDefinitionSearchResultsSkeleton';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import { RelevanceTestsService } from 'src/services/RelevanceTestsService';
import {
    Add, Download, PlayArrow,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { CreateRelevanceTestDefinitionModal } from 'src/components/RelevanceTestDefinitionSearchPage/CreateRelevanceTestDefinitionModal';
import { useQueryRelevanceTestDefinitions } from 'src/hooks/useQueryRelevanceTestDefinitions';
import { SaveLoadingButton } from 'src/components/common/buttons/SaveLoadingButton';
import { CompareTestsButton } from 'src/components/common/CompareTestsButton';

// Minimal component to handle the suspense boundary
const QueryBoundary = ({ searchTerm }: { searchTerm: string }): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const { data } = useQueryRelevanceTestDefinitions(accessToken, {
        searchTerm,
        offset: 0,
        limit: 1000,
    });

    return <RelevanceTestDefinitionSearchResults results={data?.results || []} searchTerm={searchTerm} />;
};

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(8)}`,
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(8),
}));

const ButtonGroup = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    gap: theme.spacing(4),
}));

const SearchContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        background: theme.palette.common.white,
    },
}));

export const RelevanceTestDefinitionSearchPage = (): JSX.Element => {
    const [searchInputValue, setSearchInputValue] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [openCreateTestDefinitionPop, setOpenCreateTestDefinitionPop] = useState(false);
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const [isPending, startTransition] = useTransition();
    const [successSnackBarOpen, setSuccessSnackBarOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetSearchTerm = useCallback(
        debounce((term: string) => {
            startTransition(() => {
                setDebouncedSearchTerm(term);
            });
        }, 300),
        [],
    );

    useEffect(() => {
        debouncedSetSearchTerm(searchInputValue);
        return () => {
            debouncedSetSearchTerm.cancel();
        };
    }, [searchInputValue, debouncedSetSearchTerm]);

    const handleRunAllTests = async (): Promise<void> => {
        await RelevanceTestsService.runAllRelevanceTests(accessToken);
        setSuccessMessage('You&apos;ve started a test run for all active test definitions.');
        setSuccessSnackBarOpen(true);
    };

    const handleCompareSuccess = (): void => {
        setSuccessMessage('Comparison results have been downloaded successfully.');
        setSuccessSnackBarOpen(true);
    };

    const handleExportResults = async (): Promise<void> => {
        const anchor = document.createElement('a');

        document.body.appendChild(anchor);

        const csvFile = await RelevanceTestsService.exportLatestRelevanceTestRuns(accessToken);
        const csvBlob = new Blob([csvFile], { type: 'text/csv' });
        const objectUrl = window.URL.createObjectURL(csvBlob);

        anchor.href = objectUrl;
        anchor.download = `latest_relevance_test_run_export_${(new Date()).toISOString()}.csv`;
        anchor.click();

        window.URL.revokeObjectURL(objectUrl);
    };

    const handleCreateNewTestButton = (): void => {
        setOpenCreateTestDefinitionPop(true);
    };

    const handleCreateNewTestClose = (): void => {
        setOpenCreateTestDefinitionPop(false);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchInputValue(e.target.value);
    };

    const renderSearchResults = (): JSX.Element => {
        if (isPending) {
            return <RelevanceTestDefinitionSearchResultsSkeleton />;
        }
        return (
            <QueryBoundary searchTerm={debouncedSearchTerm} />
        );
    };

    return (
        <>
            <TitledAppBar title="Relevance Test Definition Search" />
            <StyledContainer>
                <Box sx={{
                    width: '100%',
                    justifyContent: 'flex-end',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
                >
                    <ButtonGroup>
                        <SaveLoadingButton
                            aria-label="run all relevance tests"
                            Icon={PlayArrow}
                            variant="outlined"
                            onClick={handleRunAllTests}
                        >
                            Run All Tests
                        </SaveLoadingButton>
                        <CompareTestsButton onSuccess={handleCompareSuccess} />
                        <SaveLoadingButton
                            aria-label="export latest results"
                            Icon={Download}
                            variant="outlined"
                            onClick={handleExportResults}
                        >
                            Export Latest Results
                        </SaveLoadingButton>
                        <Button
                            startIcon={<Add />}
                            variant="contained"
                            onClick={handleCreateNewTestButton}
                        >
                            Create New Test
                        </Button>
                    </ButtonGroup>
                </Box>
                <SearchContainer>
                    <StyledTextField
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton edge="end" type="submit">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Search tests by keyword, product key, or product name..."
                        size="small"
                        sx={{
                            maxWidth: '600px',
                            width: '100%',
                        }}
                        value={searchInputValue}
                        onChange={handleSearchChange}
                    />
                </SearchContainer>
                {renderSearchResults()}
            </StyledContainer>
            <CreateRelevanceTestDefinitionModal
                open={openCreateTestDefinitionPop}
                onClose={handleCreateNewTestClose}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                open={successSnackBarOpen}
                onClose={(): void => setSuccessSnackBarOpen(false)}
            >
                <Alert severity="success" sx={{ width: '100%' }} onClose={(): void => setSuccessSnackBarOpen(false)}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </>
    );
};
